import React from "react";
import PhoneInput, {
  getCountries,
  type Country,
} from "react-phone-number-input";
import phoneCountryLabels from "react-phone-number-input/locale/en.json";
import "react-phone-number-input/style.css";

// components
import TextInput from "../../../components/elements/form/TextInput";
import SelectInput from "../../../components/elements/form/SelectInput";
import Button from "../../../components/elements/form/Button";

// dispatch
import { useAppDispatch, useAppSelector } from "../../../store";
import { get_countries, get_states } from "../../../service/api/locations";
import { clearStates } from "../../../store/slices/locations";
import {
  ICreateBranch,
  ICreateBranchError,
} from "../../../store/@types/company";
import { create_branch } from "../../../service/api/company";
import { useNavigate } from "react-router-dom";

const normalizeCountryName = (name: string) =>
  name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const phoneCountryByName = new Map<string, Country>(
  getCountries().map((country) => [
    normalizeCountryName(phoneCountryLabels[country] || ""),
    country,
  ]),
);

const getPhoneCountry = (countryName: string): Country | undefined =>
  phoneCountryByName.get(normalizeCountryName(countryName));

type IAddBranch = {
  id: string;
};

const AddBranch: React.FC<IAddBranch> = ({ id }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const loading = useAppSelector(({ company }) => company.loading);
  const countries = useAppSelector(({ locations }) => locations.countries);
  const states = useAppSelector(({ locations }) => locations.states);
  const locationsLoading = useAppSelector(({ locations }) => locations.loading);

  const [country, countrySet] = React.useState<string>("");
  const selectedCountry = countries.find(
    (locationCountry) => String(locationCountry.id) === country,
  );
  const phoneDefaultCountry = selectedCountry
    ? getPhoneCountry(selectedCountry.name)
    : undefined;
  const [data, dataSet] = React.useState<ICreateBranch>({
    company_address: "",
    company_id: id,
    company_phone: "",
    company_state: "",
  });
  const [error, errorSet] = React.useState<ICreateBranchError>({
    company_country: "",
    company_address: "",
    company_id: "",
    company_phone: "",
    company_state: "",
  });

  React.useEffect(() => {
    if (countries.length === 0) {
      void get_countries(dispatch);
    }
  }, [countries.length, dispatch]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    dataSet((prev) => ({ ...prev, [name]: value }));
    errorSet((prev) => ({ ...prev, [name]: "" }));
  };

  return (
    <div className="mt-6 grid gap-4 bg-white rounded-2xl p-6">
      <SelectInput
        label="Select Country"
        items={countries.map(({ id, name }) => ({
          id: String(id),
          name,
        }))}
        name="company_country"
        value={country}
        loading={locationsLoading && countries.length === 0}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
          const { value } = e.target;
          dispatch(clearStates());
          countrySet(value);
          dataSet((prev) => ({
            ...prev,
            company_state: "",
            company_phone: "",
          }));
          errorSet((prev) => ({
            ...prev,
            company_country: "",
            company_state: "",
            company_phone: "",
          }));

          if (value) {
            void get_states(dispatch, value);
          }
        }}
        status={error.company_country}
        className="capitalize"
      />

      <SelectInput
        label="Select State"
        items={states.map(({ id, name }) => ({
          id: String(id),
          name,
        }))}
        name="company_state"
        value={data.company_state}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
          const { name, value } = e.target;
          dataSet((prev) => ({ ...prev, [name]: value }));
          errorSet((prev) => ({ ...prev, [name]: "" }));
        }}
        loading={locationsLoading && Boolean(country)}
        status={error?.company_state}
      />

      <div className="w-full">
        <label htmlFor="company_phone" className="text-md font-medium">
          Company Phone
        </label>
        <div
          className={`flex w-full h-11 border items-center rounded-lg mt-1 text-gray-900 px-3 py-2 focus-within:ring focus-within:ring-primary-100/30 ${
            error.company_phone
              ? "border-[red]"
              : "border-[#e5e7eb] focus-within:border-primary-500"
          }`}
        >
          <PhoneInput
            key={`${country}-${phoneDefaultCountry || ""}`}
            id="company_phone"
            defaultCountry={phoneDefaultCountry}
            international
            countryCallingCodeEditable={false}
            value={data.company_phone || undefined}
            onChange={(value) => {
              dataSet((prev) => ({
                ...prev,
                company_phone: value ?? "",
              }));
              errorSet((prev) => ({ ...prev, company_phone: "" }));
            }}
            numberInputProps={{
              name: "company_phone",
              required: true,
              placeholder: "Company Phone",
              className:
                "PhoneInputInput w-full !outline-none px-0 py-0 text-sm border-none focus:border-transparent focus:ring-0",
            }}
            className="PhoneInput flex-1 gap-2"
          />
        </div>
      </div>

      <TextInput
        type="text"
        label="Company Address"
        name="company_address"
        value={data.company_address}
        onChange={onChange}
        placeholder="Company Address"
        required={true}
        status={error.company_address}
      />

      <div className="my-4 flex">
        <div className="w-[25%]">
          <Button
            title="Submit"
            type="button"
            loading={loading}
            className="bg-primary-500 hover:bg-primary-700"
            onHandler={() => {
              let validate: boolean = false;
              if (!data.company_address) {
                validate = true;
                errorSet((prev) => ({ ...prev, company_address: "warning" }));
              }
              if (!data.company_id) {
                validate = true;
                errorSet((prev) => ({ ...prev, company_id: "warning" }));
              }
              if (!country) {
                validate = true;
                errorSet((prev) => ({
                  ...prev,
                  company_country: "warning",
                }));
              }
              if (!data.company_phone) {
                validate = true;
                errorSet((prev) => ({ ...prev, company_phone: "warning" }));
              }
              if (!data.company_state) {
                validate = true;
                errorSet((prev) => ({ ...prev, company_state: "warning" }));
              }
              if (validate) return;
              create_branch(dispatch, data, navigate);
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default AddBranch;
